import { useState, useEffect, useCallback, useRef } from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { TemplateListPanel } from '@/components/templates/TemplateListPanel';
import { TemplateDocxViewer } from '@/components/templates/TemplateDocxViewer';
import { OADocumentViewer } from '@/components/templates/OADocumentViewer';
import { ApplicationDocSelector } from '@/components/templates/ApplicationDocSelector';
import { GenerateTemplateControl } from '@/components/templates/GenerateTemplateControl';
import { TemplateOnboardingWizard } from '@/components/templates/TemplateOnboardingWizard';
import { taskApi } from '@/services/api';
import { useNotificationStore } from '@/stores';
import { useUserStore } from '@/stores/userStore';

export type FieldValue = { description: string; example: string };

export interface TemplateItem {
  template_id: string;
  template_name: string;
  created_date?: string;
  response_type?: string;
  mapping?: {
    DynamicFields?: Record<string, string | FieldValue>;
    Sections?: Record<string, string | FieldValue>;
  };
  [key: string]: any;
}

export interface FilledTemplateItem {
  filled_template_id: string;
  filled_template_name: string;
  created_date?: string;
  [key: string]: any;
}

export interface ProsecutionDoc {
  date: string;
  rawDate: Date;
  pdfUrl: string | null;
  description: string;
  documentCode: string;
  docId: string;
  rawDoc?: any;
}

export interface SelectedDocInfo {
  docId: string;
  appNo: string;
  description: string;
  date: string;
  type: 'office_action' | 'claims' | 'supplemental' | 'uploaded_claims' | 'uploaded_oa';
  filename?: string;
  uploadId?: string;
}

export function TemplatesPage() {
  const { addNotification } = useNotificationStore();
  const userSettings = useUserStore((state) => state.userSettings);
  const updateUserSettings = useUserStore((state) => state.updateUserSettings);

  // Template state
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesInitialized, setTemplatesInitialized] = useState(false);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardExistingIds, setWizardExistingIds] = useState<Set<string>>(new Set());

  // Filled templates state
  const [filledTemplates, setFilledTemplates] = useState<FilledTemplateItem[]>([]);
  const [filledTemplatesLoading, setFilledTemplatesLoading] = useState(false);

  // Document selection state
  const [selectedAppNo, setSelectedAppNo] = useState<string | null>(null);
  const [selectedOfficeAction, setSelectedOfficeAction] = useState<SelectedDocInfo | null>(null);
  const [selectedClaims, setSelectedClaims] = useState<SelectedDocInfo | null>(null);
  const [selectedSupplementalDocs, setSelectedSupplementalDocs] = useState<SelectedDocInfo[]>([]);
  const [uploadedClaims, setUploadedClaims] = useState<SelectedDocInfo | null>(null);
  const [uploadedOA, setUploadedOA] = useState<SelectedDocInfo | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setTemplatesLoading(true);
      const res = await taskApi.getAllTemplates();
      setTemplates(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      addNotification({ type: 'error', title: 'Error', message: 'Failed to load templates' });
    } finally {
      setTemplatesLoading(false);
      setTemplatesInitialized(true);
    }
  }, [addNotification]);

  const fetchFilledTemplates = useCallback(async () => {
    try {
      setFilledTemplatesLoading(true);
      const res = await taskApi.getAllFilledTemplates();
      setFilledTemplates(Array.isArray(res) ? res : []);
    } catch (error) {
      console.error('Failed to fetch filled templates:', error);
    } finally {
      setFilledTemplatesLoading(false);
    }
  }, []);

  const defaultsSeeded = useRef(false);
  useEffect(() => {
    const init = async () => {
      if (!defaultsSeeded.current) {
        defaultsSeeded.current = true;
        try {
          await taskApi.ensureDefaultTemplates();
        } catch {
          // Non-critical — user may already have defaults or S3 source may be missing
        }
      }
      fetchTemplates();
      fetchFilledTemplates();
    };
    init();
  }, [fetchTemplates, fetchFilledTemplates]);

  // Auto-open wizard on first visit (after templates have loaded)
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!templatesInitialized || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    if (!userSettings.hasSeenTemplateOnboarding) {
      setWizardExistingIds(new Set(templates.map((t) => t.template_id)));
      setWizardOpen(true);
    }
  }, [templatesInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenWizard = useCallback(() => {
    setWizardExistingIds(new Set(templates.map((t) => t.template_id)));
    setWizardOpen(true);
  }, [templates]);

  const handleWizardOpenChange = useCallback(
    (open: boolean) => {
      setWizardOpen(open);
      if (!open) {
        updateUserSettings('hasSeenTemplateOnboarding', true);
      }
    },
    [updateUserSettings]
  );

  const handleSaveSuccess = useCallback(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return (
    <div className="flex flex-col h-full w-full bg-gradient-to-br from-slate-50 to-sky-50/30">
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* Left Panel - Template List + Upload */}
        <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
          <TemplateListPanel
            templates={templates}
            templatesLoading={templatesLoading}
            selectedTemplate={selectedTemplate}
            onSelectTemplate={setSelectedTemplate}
            fetchTemplates={fetchTemplates}
            onOpenWizard={handleOpenWizard}
          />
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Middle Panel - Document Viewers */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <ResizablePanelGroup direction="vertical">
            {/* Top - Template DOCX Viewer */}
            <ResizablePanel defaultSize={50} minSize={20}>
              <TemplateDocxViewer
                selectedTemplate={selectedTemplate}
                onSaveSuccess={handleSaveSuccess}
              />
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Bottom - OA Document Viewer */}
            <ResizablePanel defaultSize={50} minSize={20}>
              <OADocumentViewer
                selectedOfficeAction={selectedOfficeAction}
                selectedAppNo={selectedAppNo}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Right Panel - Application/Doc Selector + Generate */}
        <ResizablePanel defaultSize={25} minSize={15} maxSize={40} className="overflow-hidden">
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0 overflow-y-auto">
              <ApplicationDocSelector
                selectedAppNo={selectedAppNo}
                onAppNoChange={setSelectedAppNo}
                selectedOfficeAction={selectedOfficeAction}
                onOfficeActionChange={setSelectedOfficeAction}
                selectedClaims={selectedClaims}
                onClaimsChange={setSelectedClaims}
                selectedSupplementalDocs={selectedSupplementalDocs}
                onSupplementalDocsChange={setSelectedSupplementalDocs}
                uploadedClaims={uploadedClaims}
                onUploadedClaimsChange={setUploadedClaims}
                uploadedOA={uploadedOA}
                onUploadedOAChange={setUploadedOA}
              />
            </div>
            <div className="border-t border-slate-200">
              <GenerateTemplateControl
                selectedTemplate={selectedTemplate}
                selectedOfficeAction={selectedOfficeAction}
                selectedClaims={selectedClaims}
                selectedSupplementalDocs={selectedSupplementalDocs}
                uploadedClaims={uploadedClaims}
                uploadedOA={uploadedOA}
                selectedAppNo={selectedAppNo}
                filledTemplates={filledTemplates}
                filledTemplatesLoading={filledTemplatesLoading}
                fetchFilledTemplates={fetchFilledTemplates}
              />
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <TemplateOnboardingWizard
        open={wizardOpen}
        onOpenChange={handleWizardOpenChange}
        fetchTemplates={fetchTemplates}
        onSelectTemplate={setSelectedTemplate}
        existingTemplateIds={wizardExistingIds}
      />
    </div>
  );
}
